import { selectors } from "../store/state-selectors";
import { handlePromise } from "../infra/utils";
import { PayoutIntegrationStatus } from 'anchor-scout-common/types';
import { InvoiceIntegrationStatus } from "../models";
import isEqual from 'lodash/isEqual';
import { formatDate } from "../models/time";
export async function getAndComparePayoutSummary(api, getState, slackClient) {
  const [asyncMgmtPayouts, asyncScoutPayouts] = await Promise.all([handlePromise(api.payout.getAll()), handlePromise(api.payout.getAllFromScout({
    pageSize: 2000,
    pageNumber: 1
  }))]);
  const mgmtPayoutsResponse = asyncMgmtPayouts.data;
  const scoutPayoutsResponse = asyncScoutPayouts.data;
  if (mgmtPayoutsResponse != null && scoutPayoutsResponse != null) {
    const mgmtPayouts = mgmtPayoutsResponse.payouts;
    const scoutPayouts = scoutPayoutsResponse.payouts;
    const businessId = selectors.user.businessId(getState());
    const allDifferences = [];
    const missingInScout = [];
    mgmtPayouts.forEach(async mgmtPayout => {
      const scoutPayout = scoutPayouts.find(p => p.id() === mgmtPayout.id());
      if (scoutPayout != null) {
        const mgmtNormalized = normalizePayout(mgmtPayout);
        const scoutNormalized = normalizePayout(scoutPayout);
        if (!isEqual(mgmtNormalized, scoutNormalized)) {
          allDifferences.push("PAYOUT ".concat(mgmtPayout.id(), "\nMGMT: ").concat(JSON.stringify(mgmtNormalized), "\nSCOUT: ").concat(JSON.stringify(scoutNormalized)));
        }
      } else {
        missingInScout.push(mgmtPayout.id());
      }
    });
    if (allDifferences.length > 0 || missingInScout.length > 0) {
      const firstDifference = allDifferences[0];
      const slackMessage = "PAYOUT VALIDATION DIFFERENCES DETECTED, printing the first mismatch, Business: ".concat(businessId, ", Total Mismatches: ").concat(allDifferences.length, ", Missing in Scout: ").concat(missingInScout.join(', '), "\n\n").concat('First Difference: ' + (firstDifference ? firstDifference : 'No differences found (probably because something is missing in Scout)'));
      try {
        await slackClient.sendPayoutValidationMessage(slackMessage);
      } catch (error) {}
    }
  }
}
function normalizeIntegration(integration) {
  if (!integration) return null;
  const result = integration.accept({
    whenSynced: data => ({
      status: PayoutIntegrationStatus.Synced,
      date: data.date
    }),
    whenSyncFailed: failure => ({
      status: PayoutIntegrationStatus.SyncFailed,
      failureCode: failure.failureCode(),
      failureParam: failure.failureParam(),
      date: failure.date().getTime()
    }),
    whenPendingSync: () => ({
      status: PayoutIntegrationStatus.PendingSync
    }),
    whenSyncInProgress: () => ({
      status: PayoutIntegrationStatus.SyncInProgress
    }),
    whenDeleted: data => ({
      status: PayoutIntegrationStatus.Deleted,
      date: data.date
    })
  });
  return (result === null || result === void 0 ? void 0 : result.status) === PayoutIntegrationStatus.PendingSync ? null : result;
}
function normalizePayout(payout) {
  const payoutFailure = payout.payoutFailure();
  const qboIntegration = payout.qboIntegration();
  const xeroIntegration = payout.xeroIntegration();
  return {
    id: payout.id(),
    createdAt: payout.createdAt(),
    amount: payout.amount().amount(),
    status: payout.status(),
    formattedNumber: payout.formattedNumber(),
    isPaid: payout.isPaid(),
    payoutFailure: payoutFailure ? {
      code: payoutFailure.code,
      message: payoutFailure.message
    } : null,
    qboIntegration: normalizeIntegration(qboIntegration),
    xeroIntegration: normalizeIntegration(xeroIntegration),
    invoices: payout.invoices().map(invoice => ({
      id: invoice.id,
      type: invoice.type,
      relationshipId: invoice.relationshipId,
      number: invoice.number,
      formattedNumber: invoice.formattedNumber,
      customer: invoice.customer,
      fee: invoice.fee.amount(),
      processingFee: invoice.processingFee.amount(),
      customerProcessingFee: invoice.customerProcessingFee.amount(),
      disputeFee: invoice.disputeFee.amount(),
      serviceFeeRefund: invoice.serviceFeeRefund.amount(),
      totalFees: invoice.totalFees.amount(),
      totalAmount: invoice.totalAmount.amount(),
      depositAmount: invoice.depositAmount.amount(),
      createdOn: formatDate(invoice.createdOn.getTime()),
      integrations: (() => {
        if (!invoice.integrations) return null;
        const filtered = invoice.integrations.map(integration => {
          const result = integration.integration.accept({
            whenInvoiceRecorded: data => ({
              status: InvoiceIntegrationStatus.InvoiceRecorded,
              date: data.date
            }),
            whenVoidedRecorded: data => ({
              status: InvoiceIntegrationStatus.VoidedRecorded,
              date: data.date
            }),
            whenInvoiceSyncFailed: data => ({
              status: InvoiceIntegrationStatus.InvoiceSyncFailed,
              failureCode: data.failure.failureCode(),
              failureParam: data.failure.failureParam(),
              date: data.failure.date().getTime()
            }),
            whenPaymentSyncFailed: data => ({
              status: InvoiceIntegrationStatus.PaymentSyncFailed,
              failureCode: data.failure.failureCode(),
              failureParam: data.failure.failureParam(),
              date: data.failure.date().getTime()
            }),
            whenPendingSync: () => ({
              status: InvoiceIntegrationStatus.PendingSync
            }),
            whenPaymentRecorded: data => ({
              status: InvoiceIntegrationStatus.PaymentRecorded,
              date: data.date
            }),
            whenRefundRecorded: data => ({
              status: InvoiceIntegrationStatus.RefundRecorded,
              date: data.date,
              numRefunds: data.numRefunds
            }),
            whenRefundSyncFailed: data => ({
              status: 'RefundSyncFailed',
              failureCode: data.failure.failureCode(),
              failureParam: data.failure.failureParam(),
              date: data.failure.date().getTime()
            })
          });
          return (result === null || result === void 0 ? void 0 : result.status) === InvoiceIntegrationStatus.PendingSync ? null : {
            type: integration.type,
            integration: result
          };
        }).filter(Boolean);
        return filtered.length > 0 ? filtered : null;
      })()
    }))
  };
}